/**
 * Inline SVG icon paths (single-stroke, currentColor).
 * Centralized so screens reference the same glyphs and we ship one set.
 */
export const ICON_PATHS = {
  home: 'M3 12 L12 3 L21 12 M5 10 V20 H10 V14 H14 V20 H19 V10',
  calendar:
    'M6 3 V6 M18 3 V6 M3 9 H21 M5 6 H19 A2 2 0 0 1 21 8 V20 A2 2 0 0 1 19 22 H5 A2 2 0 0 1 3 20 V8 A2 2 0 0 1 5 6',
  trophy:
    'M7 4 H17 V6 A5 5 0 0 1 7 6 V4 Z M5 6 H3 V8 A4 4 0 0 0 7 12 M19 6 H21 V8 A4 4 0 0 0 17 12 M9 18 H15 M12 12 V18',
  profile: 'M12 12 A4 4 0 1 0 12 4 A4 4 0 0 0 12 12 Z M4 20 A8 8 0 0 1 20 20',
  play: 'M5 3 L19 12 L5 21 Z',
  refresh: 'M3 12 A9 9 0 0 1 18 6 L21 3 V9 H15 M21 12 A9 9 0 0 1 6 18 L3 21 V15 H9',
  close: 'M5 5 L19 19 M19 5 L5 19',
  back: 'M15 5 L8 12 L15 19',
  share: 'M4 12 V20 A2 2 0 0 0 6 22 H18 A2 2 0 0 0 20 20 V12 M16 6 L12 2 L8 6 M12 2 V15',
  flame:
    'M12 22 C7 22 4 18 4 14 C4 11 6 9 8 8 C8 11 10 12 10 12 C10 8 12 4 16 2 C15 5 17 7 18 9 C19 11 20 12 20 14 C20 18 17 22 12 22 Z',
  hamburger: 'M3 6 H21 M3 12 H21 M3 18 H21',
  check: 'M5 12 L10 17 L20 7',
  chevron_right: 'M9 5 L16 12 L9 19',
  bell: 'M6 16 V11 A6 6 0 0 1 18 11 V16 L20 18 H4 L6 16 Z M10 21 A2 2 0 0 0 14 21',
  globe:
    'M12 22 A10 10 0 1 1 12 2 A10 10 0 0 1 12 22 Z M2 12 H22 M12 2 C8 5 8 19 12 22 C16 19 16 5 12 2',
  settings:
    'M12 8 A4 4 0 1 1 12 16 A4 4 0 0 1 12 8 Z M19.4 15 L21 17 L17 21 L15 19.4 M4.6 9 L3 7 L7 3 L8.6 4.6 M19.4 9 L21 7 L17 3 L15 4.6 M4.6 15 L3 17 L7 21 L8.6 19.4',
  lock: 'M6 11 H18 V20 A2 2 0 0 1 16 22 H8 A2 2 0 0 1 6 20 V11 Z M8 11 V7 A4 4 0 0 1 16 7 V11',
  star: 'M12 3 L14.5 9 L21 9.5 L16 14 L17.5 21 L12 17.5 L6.5 21 L8 14 L3 9.5 L9.5 9 Z',
  crown: 'M3 7 L7 11 L12 5 L17 11 L21 7 L19 18 H5 L3 7 Z',
  trash:
    'M5 7 H19 M9 7 V5 A2 2 0 0 1 11 3 H13 A2 2 0 0 1 15 5 V7 M7 7 V20 A2 2 0 0 0 9 22 H15 A2 2 0 0 0 17 20 V7 M10 11 V18 M14 11 V18',
} as const;

export type IconName = keyof typeof ICON_PATHS;

/** Build an inline <svg> for the given icon. Returns an SVG element. */
export function icon(name: IconName, size = 24): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', ICON_PATHS[name]);
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.75');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  svg.appendChild(path);
  return svg;
}

/** SVG markup as raw string (for innerHTML / .map().join() callers). */
export function iconString(name: IconName, size = 24): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="${ICON_PATHS[name]}" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}
